import { memo, useMemo } from 'react';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import {
  Cell,
  CellText,
  Link,
  Table,
  useTable,
  type ColumnConfig,
  type SortDescriptor,
} from '@backstage/ui';
import { useRouteRef } from '@backstage/frontend-plugin-api';
import { tracksDetailsSubRouteRef } from '../../routes';
import type { InsightsTrackRow } from '@internal/backstage-plugin-pulse-common';
import { CompliancePercentageBar } from '../tracks/compliance-indicators';
import { withTeamsParam } from '../with-teams-param';
import { paginationOptions, sortCollator } from '../config';

const getSortableValue = (item: InsightsTrackRow, column: string): string => {
  switch (column) {
    case 'trackName':
      return item.trackName;
    case 'checks':
      return String(item.checkCount);
    case 'entities':
      return String(item.entityCount);
    case 'compliancePercentage':
      return String(item.compliancePercentage);
    case 'ownerEntityRef':
      return item.ownerEntityRef;
    default:
      return '';
  }
};

const sortTracks = (
  data: InsightsTrackRow[],
  sort: SortDescriptor,
): InsightsTrackRow[] => {
  const direction = sort.direction === 'descending' ? -1 : 1;
  const sortColumn = String(sort.column);

  return [...data].sort((left, right) => {
    const comparison = sortCollator.compare(
      getSortableValue(left, sortColumn).trim(),
      getSortableValue(right, sortColumn).trim(),
    );

    if (comparison !== 0) {
      return comparison * direction;
    }

    return sortCollator.compare(left.id, right.id);
  });
};

const createColumns = (
  getTrackDetailsPath: ((params: { id: string }) => string) | undefined,
  entityOwnerRefs: string[],
): ColumnConfig<InsightsTrackRow>[] => [
  {
    id: 'trackName',
    label: 'Track Name',
    isRowHeader: true,
    isSortable: true,
    defaultWidth: '34fr',
    cell: item => (
      <CellText
        title={item.trackName}
        href={
          getTrackDetailsPath
            ? withTeamsParam(
                getTrackDetailsPath({ id: item.trackId }),
                entityOwnerRefs,
              )
            : undefined
        }
      />
    ),
  },
  {
    id: 'compliancePercentage',
    label: 'Compliance',
    defaultWidth: '14fr',
    isSortable: true,
    cell: item => (
      <Cell>
        <CompliancePercentageBar percentage={item.compliancePercentage} />
      </Cell>
    ),
  },
  {
    id: 'checks',
    label: 'Checks',
    isSortable: true,
    defaultWidth: '14fr',
    cell: item => (
      <CellText title={`${item.checkCount} Checks`} color="secondary" />
    ),
  },
  {
    id: 'entities',
    label: 'Entities',
    isSortable: true,
    defaultWidth: '14fr',
    cell: item => {
      const href =
        getTrackDetailsPath && item.trackId
          ? withTeamsParam(
              `${getTrackDetailsPath({ id: item.trackId })}?view=entities`,
              entityOwnerRefs,
            )
          : undefined;

      return (
        <Cell>
          <Link
            href={href}
            color="secondary"
          >{`${item.entityCount} Entities`}</Link>
        </Cell>
      );
    },
  },
  {
    id: 'ownerEntityRef',
    label: 'Track Owner',
    isSortable: true,
    defaultWidth: '24fr',
    cell: item => (
      <Cell>
        <EntityRefLink entityRef={item.ownerEntityRef} underline="none" />
      </Cell>
    ),
  },
];

export type InsightsTracksTableProps = {
  rows: InsightsTrackRow[];
  /**
   * Owners explicitly selected on the insights page (not expanded to
   * descendant groups). Carried into each track-details link so the detail
   * page opens with the same owner selection; the detail page expands
   * descendants itself when filtering.
   */
  entityOwnerRefs?: string[];
};

const InsightsTracksTableComponent = ({
  rows,
  entityOwnerRefs = [],
}: InsightsTracksTableProps) => {
  const getTrackDetailsPath = useRouteRef(tracksDetailsSubRouteRef);
  const columns = useMemo(
    () => createColumns(getTrackDetailsPath, entityOwnerRefs),
    [getTrackDetailsPath, entityOwnerRefs],
  );

  const { tableProps } = useTable<InsightsTrackRow>({
    mode: 'complete',
    data: rows,
    initialSort: { column: 'trackName', direction: 'ascending' },
    sortFn: sortTracks,
    paginationOptions,
  });

  return (
    <Table
      columnConfig={columns}
      {...tableProps}
      emptyState="No tracks found for this group scope"
    />
  );
};

export const InsightsTracksTable = memo(InsightsTracksTableComponent);
